import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";

interface SupplierProduct {
  product_id: string;
  product_name: string;
  product_code: string;
  brand: string;
  barcode: string;
  total_purchased: number;
  total_returned: number;
  current_quantity: number;
  last_price: number;
  current_stock: number;
}

interface Supplier {
  id: string;
  name: string;
  phone: string;
  email: string;
}

interface Props {
  supplier: Supplier;
  dealerId: string;
  onClose: () => void;
  onAddProduct: () => void;
  onDataChanged: () => void;
}

export default function SupplierDetailModal({ supplier, dealerId, onClose, onAddProduct, onDataChanged }: Props) {
  const [products, setProducts] = useState<SupplierProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<string | null>(null);
  const [removeQty, setRemoveQty] = useState(1);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { data: purchases } = await supabase
        .from("purchases")
        .select("product_id, quantity, unit_price, type")
        .eq("dealer_id", dealerId)
        .eq("supplier_id", supplier.id);

      if (!purchases || purchases.length === 0) {
        setProducts([]);
        return;
      }

      // Group by product
      const productMap = new Map<string, {
        total_purchased: number;
        total_returned: number;
        last_price: number;
      }>();

      purchases.forEach((p) => {
        const existing = productMap.get(p.product_id);
        if (existing) {
          if (p.type === "purchase") {
            existing.total_purchased += Number(p.quantity);
          } else {
            existing.total_returned += Number(p.quantity);
          }
          existing.last_price = Number(p.unit_price);
        } else {
          productMap.set(p.product_id, {
            total_purchased: p.type === "purchase" ? Number(p.quantity) : 0,
            total_returned: p.type === "return" ? Number(p.quantity) : 0,
            last_price: Number(p.unit_price),
          });
        }
      });

      const productIds = [...productMap.keys()];

      const { data: prodData } = await supabase
        .from("products")
        .select("id, name, product_code, brand, barcode, stock")
        .in("id", productIds)
        .eq("dealer_id", dealerId);

      const prodInfoMap = new Map<string, {
        name: string;
        product_code: string;
        brand: string;
        barcode: string;
        stock: number;
      }>();

      (prodData || []).forEach((p) => {
        prodInfoMap.set(p.id, {
          name: p.name,
          product_code: p.product_code || "",
          brand: p.brand || "",
          barcode: p.barcode || "",
          stock: p.stock || 0,
        });
      });

      const result: SupplierProduct[] = productIds.map((pid) => {
        const info = prodInfoMap.get(pid);
        const agg = productMap.get(pid)!;
        return {
          product_id: pid,
          product_name: info?.name || "Bilinmeyen Ürün",
          product_code: info?.product_code || "",
          brand: info?.brand || "",
          barcode: info?.barcode || "",
          total_purchased: agg.total_purchased,
          total_returned: agg.total_returned,
          current_quantity: agg.total_purchased - agg.total_returned,
          last_price: agg.last_price,
          current_stock: info?.stock || 0,
        };
      });

      result.sort((a, b) => b.total_purchased - a.total_purchased);
      setProducts(result);
    } catch (_err) {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [supplier.id, dealerId]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleRemove = async (product: SupplierProduct) => {
    const qty = removeQty;
    if (qty <= 0 || qty > product.current_quantity) return;

    setRemoving(product.product_id);
    try {
      const totalAmount = product.last_price * qty;

      // Decrease stock
      const { data: prodRow } = await supabase
        .from("products")
        .select("stock")
        .eq("id", product.product_id)
        .single();

      const currentStock = Number(prodRow?.stock || 0);
      const newStock = Math.max(0, currentStock - qty);

      await supabase
        .from("products")
        .update({ stock: newStock })
        .eq("id", product.product_id);

      // Record return
      await supabase.from("purchases").insert({
        dealer_id: dealerId,
        supplier_id: supplier.id,
        product_id: product.product_id,
        quantity: qty,
        unit_price: product.last_price,
        total_amount: totalAmount,
        type: "return",
        note: `${product.product_name} ürün iadesi - ${qty} adet`,
      });

      setShowRemoveConfirm(null);
      setRemoveQty(1);
      onDataChanged();
      fetchProducts();
    } catch (_err) {
      // silently fail
    } finally {
      setRemoving(null);
    }
  };

  const totalPurchasedValue = products.reduce((sum, p) => sum + p.last_price * p.total_purchased, 0);
  const totalReturnedValue = products.reduce((sum, p) => sum + p.last_price * p.total_returned, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-background-50 rounded-2xl w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200/70 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
              <i className="ri-truck-line text-emerald-600"></i>
            </div>
            <div>
              <h4 className="text-base font-semibold text-foreground-950"><a id="supplier-detail">{supplier.name}</a></h4>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                {supplier.phone && (
                  <span className="text-xs text-foreground-500 flex items-center gap-1">
                    <i className="ri-phone-line text-xs"></i>{supplier.phone}
                  </span>
                )}
                {supplier.email && (
                  <span className="text-xs text-foreground-500 flex items-center gap-1">
                    <i className="ri-mail-line text-xs"></i>{supplier.email}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:bg-background-100 cursor-pointer shrink-0">
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Summary */}
        {!loading && products.length > 0 && (
          <div className="px-6 py-4 border-b border-background-200/70 shrink-0 bg-background-100">
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <p className="text-xs text-foreground-500">Toplam Alım</p>
                <p className="text-lg font-bold text-emerald-600">${totalPurchasedValue.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="w-px h-8 bg-background-200/70"></div>
              <div>
                <p className="text-xs text-foreground-500">Toplam İade</p>
                <p className="text-lg font-bold text-accent-500">${totalReturnedValue.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="w-px h-8 bg-background-200/70"></div>
              <div>
                <p className="text-xs text-foreground-500">Net Alım</p>
                <p className="text-lg font-bold text-foreground-950">${(totalPurchasedValue - totalReturnedValue).toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="w-px h-8 bg-background-200/70"></div>
              <div>
                <p className="text-xs text-foreground-500">Ürün Çeşidi</p>
                <p className="text-lg font-bold text-foreground-950">{products.length}</p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
                <i className="ri-box-3-line text-2xl text-foreground-300"></i>
              </div>
              <p className="text-sm text-foreground-500 mb-1">Henüz bu tedarikçiden ürün alımı yapılmamış</p>
              <p className="text-xs text-foreground-400 mb-4">Ürün alımı yapmak için aşağıdaki butonu kullanın</p>
              <button
                onClick={onAddProduct}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i> Ürün Al
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs text-foreground-500">
                  {products.length} farklı ürün
                </p>
                <button
                  onClick={onAddProduct}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-background-50 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i> Ürün Al
                </button>
              </div>
              <div className="space-y-2">
                {products.map((product) => (
                  <div key={product.product_id} className="bg-background-100 rounded-xl border border-background-200/70 p-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-background-50 border border-background-200/70 flex items-center justify-center shrink-0">
                        <i className="ri-box-3-line text-foreground-400"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground-900 truncate">{product.product_name}</p>
                            <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
                              {product.brand && <span className="text-[10px] text-foreground-400 bg-background-50 px-1.5 py-0.5 rounded">{product.brand}</span>}
                              {product.product_code && <span className="text-[10px] text-foreground-400 font-mono">{product.product_code}</span>}
                              {product.barcode && <span className="text-[10px] text-foreground-400 font-mono">{product.barcode}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xs text-foreground-500">
                              Alınan: <strong className="text-emerald-600">{product.total_purchased}</strong> adet
                            </span>
                            {product.total_returned > 0 && (
                              <span className="text-xs text-foreground-500">
                                İade: <strong className="text-accent-500">{product.total_returned}</strong> adet
                              </span>
                            )}
                            <span className="text-xs text-foreground-500">
                              Net: <strong className="text-foreground-900">{product.current_quantity}</strong> adet
                            </span>
                            <span className="text-xs text-foreground-400">
                              Stok: {product.current_stock} adet
                            </span>
                            <span className="text-xs font-medium text-emerald-600">
                              ${product.last_price.toLocaleString("en-US", { minimumFractionDigits: 2 })}/adet
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {showRemoveConfirm === product.product_id ? (
                              <div className="flex items-center gap-1.5">
                                <input
                                  type="number"
                                  min={1}
                                  max={Math.min(product.current_quantity, product.current_stock)}
                                  value={removeQty}
                                  onChange={(e) => setRemoveQty(Math.max(1, Math.min(Math.min(product.current_quantity, product.current_stock), Number(e.target.value) || 1)))}
                                  className="w-14 px-2 py-1 bg-background-50 border border-background-200/70 rounded-md text-xs text-foreground-950 text-center focus:outline-none focus:ring-2 focus:ring-accent-400"
                                />
                                <button
                                  onClick={() => handleRemove(product)}
                                  disabled={removing === product.product_id || product.current_stock <= 0}
                                  className="px-2 py-1 bg-accent-500 hover:bg-accent-600 text-background-50 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                                >
                                  {removing === product.product_id ? <i className="ri-loader-4-line animate-spin"></i> : "Onayla"}
                                </button>
                                <button
                                  onClick={() => { setShowRemoveConfirm(null); setRemoveQty(1); }}
                                  className="px-2 py-1 bg-background-50 border border-background-200/70 text-foreground-500 rounded-md text-xs cursor-pointer whitespace-nowrap hover:bg-background-200/70"
                                >
                                  İptal
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setShowRemoveConfirm(product.product_id); setRemoveQty(1); }}
                                disabled={product.current_quantity <= 0 || product.current_stock <= 0}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-accent-100 hover:bg-accent-200/70 text-accent-700 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                                title={product.current_stock <= 0 ? "Stokta ürün yok" : "İade et"}
                              >
                                <i className="ri-arrow-go-back-line text-xs"></i> Ürün Çıkar
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}